import React, {useEffect, useMemo, useState}		from 'react';

import Button										from '../ressources/components/Button';
import Select										from '../ressources/components/Select';
import Modal										from '../ressources/components/Modal';

import LANGUAGE										from '../ressources/language';
import * as API										from '../ressources/API';

import DeviceImage									from '../ressources/components/Device/DeviceImage';
import {ft_getDeviceCatalogPrice}					from '../ressources/functions/deviceFunction';
import {useAppDispatch, useAppSelector}				from '../app/hooks';
import {ft_formatMemorySize, ft_formatPrice}		from '../ressources/functions/format';
import QuantitySelect								from '../ressources/components/QuantitySelect';
import {Catalog, LanguageFiltersWithoutKeyboard}	from '../redux/catalog/interface';
import {SelectData, SelectValues}					from '../ressources/components/Select/interface';
import {addToBasket, openBasket}					from '../redux/basket/basket';
import ModalCompanyBundle from './ModalCompanyBundle';
import Badge from '../ressources/components/Badge';
import Icon from '../ressources/components/Icon';
import ft_cssVar from '../ressources/functions/getCssVar';
import img_info from '../ressources/images/icon/info-circle.svg';


interface Options {
	name: keyof Catalog['info']
	values: (string | number)[]
}

interface Props {
	deviceId: string,
	onClose: () => void,
	forceUser?: string
	mainAddress?: string
}


export function CatalogProduct(props: Props) {
	const dispatch = useAppDispatch();
	const user = useAppSelector(state => state.user.data);
	const lang = useAppSelector(state => state.settings.lang);

	const [device, set_device] = useState<Catalog | undefined>(undefined);
	const [selectedOptions, set_selectedOptions] = useState<{[val: string]: SelectValues}>({});
	const [validDevices, set_validDevices] = useState<Catalog | undefined>(undefined);
	const [selectedImage, set_selectedImage] = useState(0);
	const [quantity, set_quantity] = useState(1);

	const [modal, set_modal] = useState(false);

	const TEXTE = LANGUAGE(lang);

	const price = useMemo(() => {
		if (!validDevices)
			return 0;
		return ft_getDeviceCatalogPrice(validDevices, selectedOptions.duration as number);
	}, [validDevices, selectedOptions]);

	const options: Options[] = useMemo(() => {
		if (!device)
			return [];

		const check = (name: keyof Catalog['info'], deviceData: Catalog) => {
			const findOption = newOptions.find(find => find.name === name);
			const value = deviceData.info?.[name];

			if (!value || typeof value === 'object')
				return;

			if (!findOption)
				return newOptions.push({name, values: [value]});

			let findData = findOption.values.find(find => find === value);

			if (!findData) {
				const index = findOption.values.push(value);

				findData = findOption.values[index - 1];
			}
		};

		const durationOption: Options = {name: 'duration', values: [36]};
		const newOptions: Options[] = [];

		device.options?.forEach((deviceData) => {
			check('state', deviceData);
			check('screenSize', deviceData);
			check('stockage', deviceData);
			check('processor', deviceData);
			check('memory', deviceData);
			check('network', deviceData);
			check('graphicCard', deviceData);
			check('format', deviceData);
			check('dataVolume', deviceData);

			if (deviceData.info.keyboard && deviceData.info.keyboard.length > 0) {
				if (!newOptions.find(find => find.name === 'keyboard'))
					newOptions.push({name: 'keyboard', values: deviceData.info.keyboard || []});
			}

			if (deviceData.extendedPrice?.[0]) {
				deviceData.extendedPrice.forEach((extendedPrice) => {
					const value = extendedPrice.month;

					if (!durationOption.values.find(find => find === value))
						durationOption.values.push(value);
				});
			}
		});

		newOptions.push(durationOption);

		newOptions.forEach((filter) => {
			if (filter.values.length <= 1)
				return;

			if (filter.name === 'stockage' || filter.name === 'memory' || filter.name === 'processorCore' || filter.name === 'gpu')
				filter.values = filter.values.sort((a, b) => ft_formatMemorySize(`${a}`) - ft_formatMemorySize(`${b}`));
			else {
				filter.values = filter.values.sort((a, b) => {
					if (typeof a === 'string' && typeof b === 'string') {
						const aMatch = a.match(/\d+/)?.[0];
						const bMatch = b.match(/\d+/)?.[0];

						if (aMatch && bMatch)
							return parseInt(aMatch, 10) - parseInt(bMatch, 10);
						return a.localeCompare(b);
					} if (typeof a === 'number' && typeof b === 'number')
						return a - b;
					return 0;
				});
			}
		});

		return newOptions;
	}, [device]);

	useEffect(() => {
		if (!props.deviceId)
			return;

		API.getCatalogOption(props.deviceId, user)
		.then((response: {data?: Catalog}) => {
			if (!response.data)
				return;

			if (!response.data.options)
				response.data.options = [];

			const copy = {...response.data, options: undefined};

			response.data.options.push(copy);
			set_device(response.data);
		});
	}, [props.deviceId]);

	useEffect(() => {
		if (!device)
			return;

		const matchDevice = checkDeviceCatalogMatchAllOptions();

		set_selectedOptions({...matchDevice, duration: 36, keyboard: device.info.keyboard?.find(f => f === 'Français - AZERTY')});
	}, [device]);

	function addDeviceToBasket() {
		if (!validDevices)
			return;

		dispatch(addToBasket({
			user,
			quantity,
			device:    validDevices,
			assign:    props.forceUser,
			addressId: props.mainAddress,
			keyboard:  selectedOptions['keyboard'] as string,
			duration:  selectedOptions['duration'] as number || 36
		}));

		props.onClose();
		dispatch(openBasket());
	}

	function checkDeviceCatalogMatchAllOptions(
		newOption?: string,
		newValue?: SelectValues
	): Record<string, string | number | undefined> | null {
		if (!device?.options)
			return null;

		const isDurationValid = (duration?: number, extendedPrice?: { month: number }[]): boolean => {
			if (duration === 36)
				return true;
			return !!extendedPrice?.find(find => find.month === duration);
		};

		const isOptionValid = (option: string, value: string | number | undefined, item: Catalog): boolean => {
			const selectedValue = selectedOptions[option];

			if (option === 'duration' && !value)
				value = 36;

			if (!selectedValue && value === undefined) return true;
			if (value === undefined) return false;

			if (newOption === option) {
				return option === 'duration'
					? isDurationValid(newValue as number, item.extendedPrice)
					: newValue === value;
			}

			if (!selectedValue) return true;

			return option === 'duration'
				? isDurationValid(selectedValue as number, item.extendedPrice)
				: selectedValue === value;
		};

		const optionsToCheck: (keyof LanguageFiltersWithoutKeyboard)[] = [
			'state',
			'screenSize',
			'stockage',
			'processor',
			'memory',
			'network',
			'graphicCard',
			'dataVolume',
			'format',
			'duration'
		];

		let bestMatch: Catalog | null = null;
		let bestMatchPrice = 0;
		let maxMatchingOptions = -1;

		// eslint-disable-next-line no-restricted-syntax
		for (const item of device.options) {
			let matchingOptions = 0;

			if (newOption) {
				let allMatch = true;

				// eslint-disable-next-line no-restricted-syntax
				for (const option of optionsToCheck) {
					const value = item.info[option];
					if (isOptionValid(option, value, item))
						matchingOptions += (matchingOptions === -1) ? 0 : 1;
					else {
						if (newOption === option)
							matchingOptions = -1;
						allMatch = false;
					}
				}

				if (allMatch) {
					set_validDevices(item);
					return optionsToCheck.reduce((acc, option) => {
						acc[option as string] = item.info[option];
						return acc;
					}, {} as Record<string, string | number | undefined>);
				}
			}

			const itemPrice = ft_getDeviceCatalogPrice(item);

			if (matchingOptions > maxMatchingOptions || (matchingOptions === maxMatchingOptions && bestMatch && itemPrice < bestMatchPrice)) {
				maxMatchingOptions = matchingOptions;
				bestMatch = item;
				bestMatchPrice = itemPrice;
			}
		}

		if (bestMatch) {
			set_validDevices(bestMatch);
			return optionsToCheck.reduce((acc, option) => {
				acc[option as string] = bestMatch?.info[option];
				return acc;
			}, {} as Record<string, string | number | undefined>);
		}

		return null;
	}

	function renderImages() {
		const renderSecondary: React.ReactElement[] = [];

		device?.image?.forEach((image, i) => {
			renderSecondary.push(
				<DeviceImage
					forceIndex={i}
					catalog={device}
					imageFormat={'small'}
					onClick={() => set_selectedImage(i)}
					className={`orderNewDeviceImageListItem ${i === selectedImage && 'actual'}`}
				/>
			);
		});

		return (
			<div className={'orderNewModalDeviceImageContainer'}>
				<DeviceImage catalog={device} imageFormat={'medium'} forceIndex={selectedImage} />

				<div className={'orderNewModalDeviceImagesList'}>
					{renderSecondary}
				</div>
			</div>
		);
	}

	function renderForm() {
		const renderSelect: React.ReactElement[] = [];

		options.forEach((option, i) => {
			if (option.values.length === 0 || (option.values.length === 1 && !option.values[0]))
				return;

			const actualValue = option.values.length === 1 ? option.values[0] : selectedOptions[option.name];
			const selectData: SelectData[] = [];

			option.values.forEach((optionData) => {
				let disabled = false;

				if (option.name === 'duration' && optionData !== 36)
					disabled = !validDevices?.extendedPrice?.some(find => find.month === optionData);

				selectData.push({
					disabled,
					value:    optionData,
					render:   `${optionData} ${option.name === 'duration' ? TEXTE.global.month : ''}`
				});
			});

			renderSelect.push(
				<Select
					key={i}
					notFullWidth
					data={selectData}
					value={actualValue}
					// title={TEXTE.global[option.name]}
					readOnly={option.values.length === 1}
					onChange={(val) => {
						if (selectedOptions[option.name] === val)
							return null;
						if (option.name === 'keyboard' || option.name === 'duration')
							return set_selectedOptions({...selectedOptions, [option.name]: val});
						const matchDevice = checkDeviceCatalogMatchAllOptions(option.name, val);

						if (matchDevice && !matchDevice.duration)
							matchDevice.duration = 36;

						set_selectedOptions({...matchDevice, keyboard: selectedOptions.keyboard});
					}}
				/>
			);
		});

		return (
			<div className={'orderNewModalDeviceData'}>
				<div style={{display: 'flex', gap: 16}}>
					<span className={'orderNewModalDeviceName text-md'}>{`${device?.type !== 'sim' ? `${TEXTE.global.lease} ` : ''}${device ? device.category.name : ''}`}</span>
					{device?.category.needCharger && <Badge color={'warning'} text={TEXTE.order.noCharger} >
						<Icon
							icon={img_info}
							iconColor={ft_cssVar('warning-600')}
							popupStyle={{width: 350}}
							popup={
								<>
									<b>{TEXTE.order.chargerPopup.title}</b><br />
									{TEXTE.order.chargerPopup.text}<br />
									{device.info.brand.toLowerCase() === 'apple' ? TEXTE.order.chargerPopup.apple : ''}
								</>
							}
						/>
					</Badge>}
				</div>
				<span className={'orderNewModalDeviceText'}>{TEXTE.order.selectConfig}</span>

				<div className={'orderNewModalDeviceSelectContainer'}>
					{renderSelect}
				</div>

				<div className={'separator'} />

				<div className={'orderNewModalDeviceFooter'}>
					<div className={'orderNewModalDeviceFooterItem'}>
						<span>{TEXTE.order.deliveryDelay}</span>
						<span>{TEXTE.order.deliveryDay}</span>
					</div>

					<div className={'orderNewModalDeviceFooterItem'}>
						<span>{TEXTE.order.quantity}</span>
						<span>
							<QuantitySelect
								value={quantity}
								onClickPlus={newVal => set_quantity(newVal)}
								onClickLess={newVal => (newVal > 0 ? set_quantity(newVal) : null)}
							/>
						</span>
					</div>

					<div className={'orderNewModalDeviceFooterItem'}>
						<span>{TEXTE.global.totalPrice}</span>
						<span>{`${ft_formatPrice(price * quantity)} ${TEXTE.global.noTax} / ${TEXTE.global.month}`}</span>
					</div>

					<div className={'orderNewModalDeviceFooterButton'}>
						<Button
							disabled={!validDevices}
							onClick={addDeviceToBasket}
						>
							{TEXTE.order.addBasket}
						</Button>
						<div
							onClick={() => set_modal(true)}
							className={'orderNewModalDeviceFooterButtonPack'}
						>
							<u>{TEXTE.order.addBundle}</u>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<>
			<Modal fullWidth overflowDisabled onClose={props.onClose}>
				<div className={'orderNewModalDeviceContainer'}>
					{renderImages()}

					{renderForm()}
				</div>

			</Modal>

			{modal &&
				<ModalCompanyBundle
					shopId={validDevices?._id}
					onClose={() => set_modal(false)}
				/>
			}
		</>
	);
}